import React, { useEffect, useState } from "react";
import * as Icons from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

export const RULES = [
  { key: "grace", title: "Grace & Lateness", short: "Grace", desc: "Not late if Time In is within the grace minutes. After the grace, lateness counts from the shift start and shows on the DTR. Gets Overtime & Night Differential." },
  { key: "supervisory", title: "10.5 hrs per day", short: "10.5 h/day", desc: "No lateness. Must complete 10.5 hrs per day; short hours count as Undertime." },
  { key: "executive", title: "96 hrs per cut-off", short: "96 h/cut-off", desc: "No lateness and no daily hours rule. Must reach 96 hrs per cut-off; the shortfall counts as Undertime." },
];
const TONE = { grace: "bg-emerald-600", supervisory: "bg-blue-600", executive: "bg-violet-600" };

export const LevelRules = () => {
  const [levels, setLevels] = useState([]);
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState(false);
  const load = () => api.get("/attendance/level-rules").then(({ data }) => {
    setLevels(data.levels); setDraft(Object.fromEntries(data.levels.map((l) => [l.level, l.rule])));
  }).catch(() => {});
  useEffect(() => { load(); }, []);
  const dirty = levels.some((l) => draft[l.level] !== l.rule);
  const save = async () => {
    setBusy(true);
    try { await api.put("/attendance/level-rules", { rules: draft }); toast.success("Roles & Permission saved"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-xl border border-slate-200 mb-4 p-4" data-testid="level-rules">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <p className="text-sm font-semibold text-slate-800 flex items-center gap-1.5"><Icons.ShieldCheck className="h-4 w-4 text-blue-600" />Roles & Permission
          <button onClick={() => setInfo(!info)} className="text-slate-400 hover:text-slate-600" title="What do the rules mean?" data-testid="level-rules-info"><Icons.Info className="h-3.5 w-3.5" /></button></p>
        <div className="flex gap-2">
          {dirty && <Button size="sm" variant="outline" className="h-8" onClick={load} data-testid="level-rules-reset">Discard</Button>}
          <Button size="sm" className="h-8 bg-slate-900 hover:bg-slate-800" disabled={!dirty || busy} onClick={save} data-testid="level-rules-save">{busy ? "Saving…" : "Save"}</Button>
        </div>
      </div>
      {info && <div className="grid md:grid-cols-3 gap-2 mb-3">{RULES.map((r) => <div key={r.key} className="rounded-lg bg-slate-50 p-2.5 text-[11px] text-slate-600"><b className="text-slate-800">{r.title}</b> — {r.desc}</div>)}</div>}
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-2">{levels.map((l) => (
        <div key={l.level} className="flex items-center justify-between gap-2 rounded-lg border border-slate-100 px-3 py-2" data-testid={`level-rule-row-${l.level}`}>
          <span className="text-sm font-medium text-slate-800 truncate">{l.level}</span>
          <div className="flex rounded-md bg-slate-100 p-0.5 shrink-0">{RULES.map((r) => (
            <button key={r.key} title={r.desc} onClick={() => setDraft((d) => ({ ...d, [l.level]: r.key }))}
              className={`text-[11px] px-2 py-1 rounded ${draft[l.level] === r.key ? `${TONE[r.key]} text-white` : "text-slate-500 hover:text-slate-800"}`} data-testid={`level-rule-${l.level}-${r.key}`}>{r.short}</button>
          ))}</div>
        </div>
      ))}</div>
    </div>
  );
};
